/**
 * Small readers for the final report: raw module values (typed `unknown`, as
 * the runner relays them) turned into text, colours and times. Every one
 * answers `null` for what it cannot read rather than guessing.
 */

const HEX = /^#?([0-9a-fA-F]{6})$/;

/** `#RRGGBB` for a 6-digit hex, with or without its `#`; anything else is `null`. */
export function swatchHex(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const match = HEX.exec(value.trim());
    return match?.[1] === undefined ? null : `#${match[1].toUpperCase()}`;
}

/** A value as text: strings trimmed, numbers and booleans written; empty and objects are `null`. */
export function text(value: unknown): string | null {
    if (typeof value === 'string') return value.trim() === '' ? null : value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (typeof value === 'boolean') return value ? 'yes' : 'no';
    return null;
}

/** WCAG 2.1 relative luminance of a validated `#RRGGBB`. */
function luminance(hex: string): number {
    const channels = [1, 3, 5].map((start) => {
        const value = parseInt(hex.slice(start, start + 2), 16) / 255;
        return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    });
    const [r = 0, g = 0, b = 0] = channels;
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** White or near-black, whichever reads on the given fill; white on no fill. */
export function readableTextOn(hex: string | null): string {
    if (hex === null) return '#FFFFFF';
    return luminance(hex) > 0.179 ? '#1A1A1A' : '#FFFFFF';
}

/** AA for normal text, the threshold the Branding migrator checks against. */
const AA_CONTRAST = 4.5;

export function contrastNote(ratio: unknown): string | null {
    if (typeof ratio !== 'number' || !Number.isFinite(ratio)) return null;
    const verdict = ratio >= AA_CONTRAST ? 'meets AA (≥4.5)' : 'below AA (4.5)';
    return `Contrast ${String(ratio)}:1 · ${verdict}`;
}

/**
 * A typeface name as an inline `font-family`, or `null`. Letters, digits,
 * spaces and hyphens only: the name comes from a customer's org.
 */
export function cssFontFamily(name: string | null): string | null {
    if (name === null || !/^[\p{L}\p{N} -]{1,60}$/u.test(name)) return null;
    return `"${name}", ui-sans-serif, system-ui, sans-serif`;
}

/**
 * `host/…/file.png`: a long URL as the two parts a reader recognises. Anything
 * that does not parse as an http(s) URL is returned as is.
 */
export function shortUrl(value: string): string {
    let url: URL;
    try {
        url = new URL(value);
    } catch {
        return value;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return value;
    const segments = url.pathname.split('/').filter((segment) => segment.length > 0);
    const file = segments[segments.length - 1];
    if (file === undefined) return url.host;
    return segments.length > 1 ? `${url.host}/…/${file}` : `${url.host}/${file}`;
}

/**
 * `2026-10-01 10:05 UTC`. Fixed, not the reader's locale: two people
 * comparing one shared report must read the same times.
 */
export function formatWhen(iso: string | null): string {
    if (iso === null) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

/** `22 ms`, `2.1 s`, `3 min 4 s`; `null` when either end is missing or reversed. */
export function durationText(start: string | null, end: string | null): string | null {
    if (start === null || end === null) return null;
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    if (ms < 1000) return `${String(ms)} ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
    const seconds = Math.round(ms / 1000);
    return `${String(Math.floor(seconds / 60))} min ${String(seconds % 60)} s`;
}

export const plural = (count: number, word: string, many = `${word}s`): string =>
    `${count.toLocaleString('en-US')} ${count === 1 ? word : many}`;
